// Lightweight wrapper around fetch that attaches HMAC-signed headers
// Signature format:
// canonical = `${method}\n${path}\n${timestamp}\n${clientFingerprint}`
// header names: X-Widget-Signature, X-Widget-Timestamp, X-Widget-Client-Info

export type SignedFetchInit = RequestInit & {
  // Secret used for HMAC. Required.
  widgetKey: string;
  // Optional explicit path for canonical string. If omitted, derived from URL.pathname
  path?: string;
  // Optional client IP if caller has it; otherwise 'unknown' is used.
  ipAddress?: string;
};

// Generate simple hash (matches C# GenerateSimpleHash)
function generateSimpleHash(input: any) {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16); // Return as hex
}

export async function signedFetch(
  input: string | URL,
  init: SignedFetchInit
): Promise<Response> {
  const url =
    typeof input === "string"
      ? new URL(input, window.location.href)
      : new URL(input.toString());

  const method = (init.method || "GET").toUpperCase();

  // get current timestamp in UTC
  const utcString = Date.now().toString();
  const timestamp = utcString;
  const userAgent =
    (navigator && navigator.userAgent).substring(0, 50) || "unknown";
  // Generate nonce (random string)
  const nonce = Math.random().toString(36).substring(2, 15);

  // Build payload to hash (matches C# expectedPayload)
  const payload = `${init.widgetKey}:${timestamp}:${nonce}:${userAgent}`;

  const signature = generateSimpleHash(payload);

  // Merge headers, preserving any provided ones
  const headers = new Headers(init.headers || {});

  headers.delete("X-Timestamp");
  headers.delete("X-Nonce");
  headers.delete("X-Signature");
  headers.delete("X-Widget-Version");
  headers.delete("User-Agent");
  headers.delete("Content-Type");

  headers.set("X-Timestamp", String(timestamp));
  headers.set("X-Nonce", nonce);
  headers.set("X-Signature", signature);
  headers.set("X-Widget-Version", "1.0");
  headers.set("User-Agent", userAgent);
  headers.set("Content-Type", "application/json");

  const { widgetKey, path: _p, ipAddress, ...rest } = init;
  return fetch(url.toString(), { ...rest, method, headers });
}
